import {defineMessages} from 'react-intl'

/**
 * Catálogo de la bandeja de entrada (`/inbox`). Namespace raíz de la Fase 1.9:
 * `comms.*` — mensajería (chat, campañas, plantillas, destinatarios,
 * automatizaciones). No se usa `crm.*` porque en el menú lateral "CRM" agrupa
 * Leads/Contactos/Cotizaciones/Pólizas, que son otra cosa.
 *
 * WhatsApp y SMS son nombres de canal: no se traducen.
 */
export const CHAT_MESSAGES = defineMessages({
  // Página
  inbox: {id: 'MENU.item.inbox', defaultMessage: 'Inbox'},

  // Lista de conversaciones
  conversations: {id: 'comms.chat.list.title', defaultMessage: 'Conversations'},
  refresh: {id: 'comms.chat.list.refresh', defaultMessage: 'Refresh conversations'},
  newChat: {id: 'comms.chat.list.newChat', defaultMessage: 'New Chat'},
  searchPlaceholder: {
    id: 'comms.chat.list.searchPlaceholder',
    defaultMessage: 'Search by name or phone…',
  },
  mode: {id: 'comms.chat.list.mode', defaultMessage: 'Mode:'},
  modeMine: {id: 'comms.chat.list.mode.mine', defaultMessage: 'My'},
  modeTeam: {id: 'comms.chat.list.mode.team', defaultMessage: 'Team'},
  modeMineTooltip: {
    id: 'comms.chat.list.mode.mineTooltip',
    defaultMessage: 'My conversations',
  },
  modeTeamTooltip: {
    id: 'comms.chat.list.mode.teamTooltip',
    defaultMessage: 'My conversations plus my supervised team',
  },
  modeUnassigned: {
    id: 'comms.chat.list.mode.unassigned',
    defaultMessage: 'Unassigned conversations in my organization',
  },
  modeAll: {
    id: 'comms.chat.list.mode.all',
    defaultMessage: 'All conversations in my organization',
  },
  pageOf: {
    id: 'comms.chat.list.pageOf',
    defaultMessage: 'Page {current} of {total} ({count} total)',
  },
  modeGeneral: {id: 'common.value.general', defaultMessage: 'General'},
  modeAllLabel: {id: 'common.pagination.all', defaultMessage: 'All'},
  prevPage: {id: 'comms.chat.list.prevPage', defaultMessage: 'Previous page'},
  nextPage: {id: 'comms.chat.list.nextPage', defaultMessage: 'Next page'},
  noMatches: {
    id: 'comms.chat.list.noMatches',
    defaultMessage: 'No conversations match your filters',
  },
  noConversations: {id: 'comms.chat.list.empty', defaultMessage: 'No active conversations'},
  noMessages: {id: 'comms.chat.list.noMessages', defaultMessage: 'No messages'},
  avatarAlt: {id: 'comms.chat.list.avatarAlt', defaultMessage: 'Profile picture'},

  // Filtros avanzados
  filters: {id: 'common.action.filter', defaultMessage: 'Filter'},
  advancedFilters: {id: 'comms.chat.filters.title', defaultMessage: 'Advanced Filters'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  unreadOnly: {id: 'comms.chat.filters.unreadOnly', defaultMessage: 'Unread only'},
  channels: {id: 'comms.field.channels', defaultMessage: 'Channels'},
  dateRange: {id: 'comms.chat.filters.dateRange', defaultMessage: 'Date Range'},
  tags: {id: 'comms.tags.label', defaultMessage: 'Tags'},
  noTagsAvailable: {id: 'comms.chat.filters.noTags', defaultMessage: 'No tags available'},
  assignedAgent: {id: 'comms.chat.filters.assignedAgent', defaultMessage: 'Assigned agent'},
  yourTeam: {id: 'comms.chat.filters.yourTeam', defaultMessage: '(your team)'},
  organization: {id: 'comms.field.organization', defaultMessage: 'Organization'},
  clearFilters: {id: 'common.action.clearFilters', defaultMessage: 'Clear filters'},
  apply: {id: 'comms.chat.filters.apply', defaultMessage: 'Apply'},

  // Nueva conversación
  newConversation: {id: 'comms.chat.new.title', defaultMessage: 'Start New Conversation'},
  searchContact: {id: 'comms.chat.new.searchContact', defaultMessage: 'Search Contact'},
  newSearchPlaceholder: {
    id: 'comms.chat.new.placeholder',
    defaultMessage: 'Name, Phone or Email…',
  },
  newHint: {
    id: 'comms.chat.new.hint',
    defaultMessage: 'Search for a contact by name, phone or email',
  },
  newNoResults: {
    id: 'comms.chat.new.noResults',
    defaultMessage: 'No contacts found for “{query}”',
  },

  // Etiquetas
  manageTags: {id: 'comms.tags.manage', defaultMessage: 'Manage Tags'},
  newTag: {id: 'comms.chat.tags.new', defaultMessage: 'New Tag'},
  availableTags: {id: 'comms.chat.tags.available', defaultMessage: 'Available Tags'},
  noTagsYet: {id: 'comms.chat.tags.empty', defaultMessage: 'No tags created yet.'},
  createNewTag: {id: 'comms.chat.tags.create', defaultMessage: 'Create New Tag'},
  tagNamePlaceholder: {id: 'comms.chat.tags.placeholder', defaultMessage: 'Ex: VIP Client'},
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  color: {id: 'common.field.color', defaultMessage: 'Color'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  saving: {id: 'comms.chat.tags.saving', defaultMessage: 'Saving…'},
  create: {id: 'comms.chat.tags.createAction', defaultMessage: 'Create'},

  // Ventana de conversación
  selectConversation: {
    id: 'comms.chat.window.selectConversation',
    defaultMessage: 'Select a conversation to start chatting',
  },
  waActive: {
    id: 'comms.chat.window.waActive',
    defaultMessage: 'WhatsApp active ({hours}h remaining)',
  },
  waExpired: {id: 'comms.chat.window.waExpired', defaultMessage: 'WhatsApp session expired'},
  waExpiredHint: {
    id: 'comms.chat.window.waExpiredHint',
    defaultMessage: 'WhatsApp session expired. Use SMS or send a Template to reopen.',
  },
  via: {id: 'comms.chat.window.via', defaultMessage: 'Via:'},
  sendTemplate: {id: 'comms.chat.window.sendTemplate', defaultMessage: 'Send Template'},
  sendTemplateTooltip: {
    id: 'comms.chat.window.sendTemplateTooltip',
    defaultMessage: 'Send WhatsApp Template to reopen conversation',
  },
  tagsManager: {id: 'comms.chat.window.tagsManager', defaultMessage: 'Tags Manager'},
  attachmentAlt: {id: 'comms.chat.window.attachmentAlt', defaultMessage: 'Attachment'},
  attachmentN: {id: 'comms.chat.window.attachmentN', defaultMessage: 'Attachment {index}'},
  noFilteredMessages: {
    id: 'comms.chat.window.noFilteredMessages',
    defaultMessage: 'No messages matching selected filters',
  },
  typeMessage: {id: 'comms.chat.window.typeMessage', defaultMessage: 'Type a message'},
  previewAlt: {id: 'comms.chat.window.previewAlt', defaultMessage: 'Preview'},
  removeFile: {id: 'comms.chat.window.removeFile', defaultMessage: 'Remove file'},
  attachFile: {id: 'comms.chat.window.attachFile', defaultMessage: 'Attach file'},
  expiredSuffix: {id: 'comms.chat.window.expiredSuffix', defaultMessage: '(expired)'},
  sendUsing: {id: 'comms.chat.window.sendUsing', defaultMessage: 'Send using…'},
  defaultChannel: {id: 'comms.chat.window.defaultChannel', defaultMessage: 'Default Channel'},

  dropFile: {id: 'comms.chat.window.dropFile', defaultMessage: 'Drop file here'},
  send: {id: 'common.action.send', defaultMessage: 'Send'},
  fileSize: {id: 'comms.chat.window.fileSize', defaultMessage: '{size} KB'},
  templates: {id: 'MENU.item.templates', defaultMessage: 'Templates'},

  // Respuestas rápidas
  quickReplies: {id: 'comms.chat.quick.title', defaultMessage: 'Quick Replies'},
  savedReplies: {id: 'comms.chat.quick.saved', defaultMessage: 'Saved replies'},
  placeholdersHint: {
    id: 'comms.chat.quick.placeholdersHint',
    defaultMessage: 'Use placeholders like {tokens}.',
  },
  loadingQuickReplies: {
    id: 'comms.chat.quick.loading',
    defaultMessage: 'Loading quick replies…',
  },
  syncingData: {id: 'comms.chat.quick.syncing', defaultMessage: 'Syncing data…'},
  noQuickReplies: {
    id: 'comms.chat.quick.empty',
    defaultMessage: 'No quick replies yet. Create your first one on the right.',
  },
  scopeMine: {id: 'comms.chat.quick.scopeMine', defaultMessage: 'My reply'},
  scopeOrganization: {id: 'comms.field.organization', defaultMessage: 'Organization'},
  use: {id: 'common.action.use', defaultMessage: 'Use'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  archive: {id: 'comms.chat.quick.archive', defaultMessage: 'Archive'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  editQuickReply: {id: 'comms.chat.quick.editTitle', defaultMessage: 'Edit quick reply'},
  newQuickReply: {id: 'comms.chat.quick.newTitle', defaultMessage: 'New quick reply'},
  title: {id: 'common.field.title', defaultMessage: 'Title'},
  titlePlaceholder: {
    id: 'comms.chat.quick.titlePlaceholder',
    defaultMessage: 'Example: Welcome with link',
  },
  scope: {id: 'common.field.scope', defaultMessage: 'Scope'},
  scopeOnlyMe: {id: 'comms.chat.quick.onlyMe', defaultMessage: 'Only for me'},
  adminsOnly: {id: 'comms.chat.quick.adminsOnly', defaultMessage: '(admins only)'},
  message: {id: 'common.field.message', defaultMessage: 'Message'},
  emojis: {id: 'comms.chat.quick.emojis', defaultMessage: 'Emojis'},
  commonEmojis: {id: 'comms.chat.quick.commonEmojis', defaultMessage: 'Common emojis'},
  insertVariable: {id: 'comms.chat.quick.insertVariable', defaultMessage: 'Insert variable'},
  variables: {id: 'comms.chat.quick.variables', defaultMessage: 'Variables'},
  personalizedHint: {
    id: 'comms.chat.quick.personalizedHint',
    defaultMessage:
      'The message is personalized when used in chat. Placeholders: {tokens}, etc.',
  },
  cancelEdit: {id: 'comms.chat.quick.cancelEdit', defaultMessage: 'Cancel edit'},
  update: {id: 'common.action.update', defaultMessage: 'Update'},
  save: {id: 'common.action.save', defaultMessage: 'Save'},

  // Toasts del hook de datos
  errorLoadingConversations: {
    id: 'comms.chat.toast.errorConversations',
    defaultMessage: 'Error loading conversations',
  },
  errorLoadingMessages: {
    id: 'comms.chat.toast.errorMessages',
    defaultMessage: 'Error loading messages',
  },
  errorSendingMessage: {
    id: 'comms.chat.toast.errorSending',
    defaultMessage: 'Error sending message',
  },
})

/** Nombres de canal: marcas, no se traducen (guía §17.3). */
export const CHANNEL_WHATSAPP = 'WhatsApp'
export const CHANNEL_SMS = 'SMS'
/** Abreviatura del badge de canal y inicial de respaldo: diseño, no texto. */
export const CHANNEL_WA_SHORT = 'WA'
export const UNKNOWN_INITIAL = '?'
/** Emoji del botón de emojis: es el propio icono, no texto. */
export const EMOJI_SMILEY = '😀'
